/**
 * Path plumbing for the /v2 revamp.
 *
 * Every internal navigation inside src/newui/ MUST go through `v2Path()`. A
 * hardcoded absolute path silently drops the user out of /v2 back into v1
 * mid-flow, which is the single most likely bug in this project. Grep
 * src/newui/ for `to="/` and `navigate('/` before calling any screen done.
 */

/** Root of the gated v2 application shell. */
export const V2_BASE = '/v2/app';

/** The public-ish marketing landing page. */
export const V2_LANDING = '/v2';

/**
 * Show feature-gated nav items as locked instead of hiding them (v1's
 * `canSeeNavItem` hides them completely). Grants no access and adds no API
 * call — a locked item is a real NavLink to the real route, where the
 * route-level gate stays the single authority. Set `false` to restore v1's
 * hide-completely behaviour byte-for-byte.
 */
export const SHOW_LOCKED_NAV_ITEMS = true;

/**
 * Paths v2 owns, each resolving to its `/v2/app` twin.
 *
 * Mostly v1 paths that v2 rebuilt, plus surfaces that only ever existed in v2
 * — those have no v1 page to twin, but they still need to be listed or
 * v2Path() cannot resolve them and warns.
 *
 * Keep in sync with routes.ts — every entry here has a route there.
 */
const V2_TWINNED_PATHS: readonly string[] = [
  '/notifications',
  '/waitlist',
  // v2-only: there is no v1 upcoming-sales page.
  '/upcoming-sales',
  '/drops',
  '/tickets',
  '/listings',
  '/transfers',
  '/accounts',
  '/dice/settings',
  '/dice/signup',
  '/dice/success',
  '/profile',
  '/billing',
  '/seats',
  '/setup',
  '/support',
];

/**
 * Public, layout-light routes that are deliberately NOT duplicated into v2.
 * v2 links straight at them; duplicating them would be five files of pure risk.
 */
const PUBLIC_PASSTHROUGH: readonly string[] = [
  '/login',
  '/admin-login',
  '/help',
  '/privacy',
  '/terms',
];

/**
 * Map a v1 absolute path to its v2 equivalent.
 *
 *   v2Path('/accounts')            === '/v2/app/accounts'
 *   v2Path('/billing?status=ok')   === '/v2/app/billing?status=ok'
 *   v2Path('/help')                === '/help'          (not duplicated)
 *
 * Unknown paths are returned untouched so a typo degrades to v1 navigation
 * rather than a 404, and warns loudly in development.
 */
export function v2Path(p: string): string {
  if (!p.startsWith('/')) return p;

  const splitAt = p.search(/[?#]/);
  const pathname = splitAt === -1 ? p : p.slice(0, splitAt);
  const suffix = splitAt === -1 ? '' : p.slice(splitAt);

  if (pathname.startsWith(V2_BASE) || pathname === V2_LANDING) return p;
  if (PUBLIC_PASSTHROUGH.includes(pathname)) return p;

  const match = V2_TWINNED_PATHS.find(
    (twin) => pathname === twin || pathname.startsWith(`${twin}/`),
  );
  if (match) return `${V2_BASE}${pathname}${suffix}`;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[newui] v2Path() has no mapping for "${p}" — falling through to v1.`);
  }
  return p;
}

/**
 * v1's BILLING_BYPASS_PATHS, mirrored onto the /v2/app prefix. An account with
 * no active subscription and no admin comp is redirected to billing from every
 * other route, exactly as in v1's MainLayout.
 */
export const V2_BILLING_BYPASS_PATHS: readonly string[] = [
  '/billing',
  '/profile',
  '/dice/signup',
  '/dice/success',
  // Support must be reachable by exactly the people the redirect catches —
  // a declined card or a lapsed plan is the most common reason to write in.
  '/support',
].map((p) => `${V2_BASE}${p}`);
